import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

import colors from "core/colors";

const createHelpers = (width, height, css) => {
    // somehow sizes is ending up in markup, even if it is not a valid svg attribute
    // until we have a better solution, just render it empty, instead to '[Object object]'
    const sanitizeSizes = sizes =>
        Object.defineProperty(sizes, "toString", {
            value: () => "",
            enumerable: false
        });

    const getDimensions = (size, sizes) => {
        if (
            size &&
            typeof size.width === "number" &&
            typeof size.height === "number"
        ) {
            return size;
        }

        return size && sizes[size] ? sizes[size] : { width, height };
    };

    const getCss = (size, sizes, fillColor, fillColorRule, noStyles) => {
        if (noStyles) {
            return "";
        }

        const dimensions = getDimensions(size, sizes);
        const fillRule =
            fillColor && fillColorRule
                ? `${fillColorRule}{ fill: ${fillColor}; }`
                : "";

        return css`
            width: ${dimensions.width}px;
            height: ${dimensions.height}px;
            ${fillRule}
        `;
    };

    const propsToCss = ({ size, sizes, fillColor, fillColorRule, noStyles }) =>
        getCss(size, sizes, fillColor, fillColorRule, noStyles);

    return {
        getCss,
        getDimensions,
        propsToCss,
        sanitizeSizes
    };
};

const width = "100";
const height = "40";
const viewBox = "0 0 100 40";

const { getDimensions, getCss, propsToCss, sanitizeSizes } = createHelpers(
    width,
    height,
    css
);

const sizes = sanitizeSizes({});

const Image = styled.svg`
    ${propsToCss}
`;

const children = (
    <Fragment>
        <path
            fill={colors.secondary.orca}
            fillRule="evenodd"
            d="M58.999 11.16c.102.101.16.24.16.383l.01 12.975-.01.01v-.004l-4.993 4.994h.003-12.98a.547.547 0 01-.544-.545v-17.43a.545.545 0 01.54-.543h17.43c.144 0 .282.058.384.16zm-8.056 6.2a4.455 4.455 0 00-.83-.099h-2.855a.219.219 0 00-.218.223v5.556a.216.216 0 00.218.218h1.376a.22.22 0 00.22-.224v-1.405h.778l.98 1.526a.214.214 0 00.181.101h1.844a.097.097 0 00.082-.151c-.292-.453-1.132-1.76-1.16-1.808l.134-.095.178-.125c.924-.627 1.027-1.874.498-2.724a2.23 2.23 0 00-1.425-.993zm-2.072 2.805h1.255a.795.795 0 00.33-.078.655.655 0 00.362-.613.586.586 0 00-.334-.57.77.77 0 00-.408-.098H48.87v1.359zm32.977-9.112h17.786c.202 0 .366.164.366.366v17.786a.366.366 0 01-.366.366H81.848a.366.366 0 01-.366-.366V11.419c0-.202.164-.366.366-.366zm8.413 10.72v-4.225h-.001a.22.22 0 00-.221-.22h-1.36a.222.222 0 00-.223.22v5.549a.22.22 0 00.218.22h4.123a.221.221 0 00.22-.22v-1.104a.22.22 0 00-.22-.22H90.26zm-28.92-10.707h17.785c.202 0 .366.164.366.366v17.786a.366.366 0 01-.366.366H61.34a.366.366 0 01-.366-.366V11.432c0-.202.164-.366.366-.366zm12.396 12.068l-2.666-5.807-1.682.002-2.699 5.806a.135.135 0 00.009.129.133.133 0 00.112.062h1.613a.264.264 0 00.244-.162l.26-.601h2.58l.25.6a.26.26 0 00.245.161h1.613a.134.134 0 00.121-.19zm-3.524-3.7l.626 1.656H69.58l.632-1.656zm-49.524-8.368h17.785c.203 0 .366.164.366.366v17.786a.366.366 0 01-.366.366H20.69a.366.366 0 01-.366-.366V11.432c0-.202.164-.366.366-.366zm11.897 6.224a.215.215 0 00-.083-.016h-1.405a.218.218 0 00-.217.215v2.976c0 .17-.023.338-.065.503a1.071 1.071 0 01-.898.834 1.532 1.532 0 01-1.021-.125 1.13 1.13 0 01-.51-.58 2.468 2.468 0 01-.161-.923V17.49a.214.214 0 00-.138-.198.216.216 0 00-.083-.015h-1.368a.215.215 0 00-.216.215v2.713c-.002.288.016.577.055.863.072.526.305 1.017.668 1.405.554.593 1.27.83 2.053.89.373.032.75.018 1.12-.044.604-.08 1.169-.35 1.612-.769a2.752 2.752 0 00.792-1.936V17.49a.214.214 0 00-.135-.2zm-14.434-6.275H.366A.366.366 0 000 11.38v17.785c0 .202.164.366.366.366h17.786a.366.366 0 00.366-.366V11.381a.366.366 0 00-.366-.367zM12.8 17.526v5.584a.201.201 0 01-.2.2h-1.452a.2.2 0 01-.2-.2V20.33l-1.782 2.455c-.12-.161-1.745-2.439-1.832-2.58v2.922a.2.2 0 01-.2.198H5.726a.2.2 0 01-.2-.2v-5.592a.201.201 0 01.2-.2h1.29a.203.203 0 01.161.082L9.15 20.13l1.998-2.72a.202.202 0 01.162-.082h1.29a.201.201 0 01.2.2z"
            clipRule="evenodd"
            key="key-0"
        />
    </Fragment>
);

const defaultProps = {
    children,
    viewBox,
    fillColor: null,
    fillColorRule: "&&& path, &&& use, &&& g",
    sizes,
    size: null
};

Image.propTypes /* remove-proptypes */ = {
    fillColor: PropTypes.string,
    fillColorRule: PropTypes.string,
    viewBox: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            height: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired
        })
    ]),
    sizes: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
    })
};

export default Object.assign(Image, {
    getDimensions,
    getCss,
    defaultProps,
    displayName: "Mural"
});
